/**
* @author zjc[beica1@outook.com]
* @date 2021/8/16 14:11
* @description
*   TheTradePanel.vue of WeTrade
*/
<template>
  <div class="c-title f-md fill">
    <div class="spread df-col">
      <div class="flex-1 scroll-y px-16">
        <TheRowTemplate>
          <template #label>
            <t path="quickplay_7">Direction</t>
          </template>
          <div class="d-f">
            <t as="button" path="quickplay_8" class="btn sm flex-1 br-sm" :class="[isBuy && 'success']"
               @click="buy">Buy
            </t>
            <t as="button" path="quickplay_9" class="btn sm flex-1 br-sm ml-16"
               :class="[!isBuy && 'danger']" @click="sell">Sell
            </t>
          </div>
        </TheRowTemplate>
        <TheRowTemplate>
          <template #label>
            <t path="quickplay_10" class="mt-16">Volume</t>
          </template>
          <TheFiniteSelector :options="products" @change="v => product = v">
            <template #default="{item}">
              <money :v="item.price" class="f-bold" />
              <!--              ({{item.specification}})-->
            </template>
          </TheFiniteSelector>
        </TheRowTemplate>
        <Line class="my-16" />
        <CurrentQuote :code="product && product.code" #="{symbol}">
          <RealTimeQuote :symbol="symbol" #="{price}">
            <TheRowTemplate>
              <template #label>
                <t path="quickplay_11" class="mt-16">Take profit</t>
              </template>
              <span class="c-success">
            +<money :v="prop('price', product)" /> @{{calcStopPrice(price, true)}}
          </span>
            </TheRowTemplate>
            <TheRowTemplate>
              <template #label>
                <t path="quickplay_12" class="mt-16">Stop loss</t>
              </template>
              <span class="c-danger">
            -<money :v="prop('price', product)" /> @{{calcStopPrice(price)}}
          </span>
            </TheRowTemplate>
          </RealTimeQuote>
        </CurrentQuote>
        <Line class="my-16" />
        <TheRowTemplate>
          <template #label>
            <t path="quickplay_13">Commission</t>
          </template>
          <money :v="prop('fee', product)" />
        </TheRowTemplate>
        <p class="c-tip my-16">
          <Icon name="warn" sm />
          <t path="quickplay_14" class="f-sm ml-4">
            Commissions will only be charged when profit is made
          </t>
        </p>
      </div>
      <div class="d-f">
        <Button :disabled="!product" class="flex-2 btn primary f-bold f-lg"
                @click="commit" :progress="progress">
          <t path="quickplay_15">Place order</t>
        </Button>
        <TheAsset #="{ freeMargin }">
          <router-link v-if="cost > freeMargin" to="/in" class="flex-1">
            <button class="block btn danger f-bold f-lg">
              <t path="trade_12" #="{td}" custom>{{td || 'Deposit'}}</t>
            </button>
          </router-link>
          <span v-else></span>
        </TheAsset>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Button from '@/components/Button.vue'
import Icon from '@/components/Icon.vue'
import Line from '@/components/Line.vue'
import { showAlert } from '@/components/popup/popup'
import { events } from '@/config'
import useRequest from '@/hooks/useRequest'
import { translate } from '@/i18n'
import TheFiniteSelector from '@/modules/fastTrade/components/TheFiniteSelector.vue'
import TheRowTemplate from '@/modules/fastTrade/components/TheRowTemplate.vue'
import { Product } from '@/modules/fastTrade/fastTrade'
import { create } from '@/modules/fastTrade/fastTrade.api'
import TheAsset from '@/modules/user/components/TheAsset.vue'
import CurrentQuote from '@/provider/CurrentQuote.vue'
import RealTimeQuote from '@/provider/RealtimeQuote.vue'
import { TradeDirection } from '@/types'
import { on } from 'essential/tools/event'
import { add } from 'essential/tools/math'
import { computed, defineComponent, shallowRef, watchEffect } from 'vue'

export default defineComponent({
  name: 'TheTradePanel',
  components: {
    TheAsset,
    RealTimeQuote,
    CurrentQuote,
    Button,
    TheFiniteSelector,
    TheRowTemplate,
    Icon,
    Line,
  },
  emits: ['created', 'typeChanged'],
  setup (props, ctx) {
    const type = shallowRef(TradeDirection.BUY)
    const product = shallowRef<Product | null>(null)
    const products = shallowRef<Product[]>([])
    // const isPrime = computed(() => state.prime.member === YesOrNo.YES)

    const isBuy = computed(() => type.value === TradeDirection.BUY)

    const cost = computed(() => add(product.value?.price ?? 0, product.value?.fee ?? 0))

    on(events.fastTradeProductChange, (_products: Product[]) => {
      products.value = _products
    })

    const buy = () => {
      type.value = TradeDirection.BUY
    }

    const sell = () => {
      type.value = TradeDirection.SELL
    }

    watchEffect(() => {
      ctx.emit('typeChanged', type.value)
    })

    const [_commit, progress] = useRequest(create)

    const commit = () => {
      // if (!isDemoAccount.value && !isPrime.value) {
      //   openDialog(LimitDialogTemplate, {
      //     i18nPath: 'quickplay_52',
      //   })
      //   return false
      // } else {
      //   return _commit({
      //     code: product.value?.code,
      //     price: product.value?.price,
      //     voucher: 0,
      //     type: type.value,
      //   }).then(resp => {
      //     const pointsDesc = resp.rewardPoints ? ` [+${resp.rewardPoints}pts]` : ''
      //     showAlert(translate('quickplay_16', 'The order has been placed') + pointsDesc)
      //     ctx.emit('created', resp)
      //   })
      // }
      return _commit({
        code: product.value?.code,
        price: product.value?.price,
        voucher: 0,
        type: type.value,
      }).then(resp => {
        const pointsDesc = resp.rewardPoints ? ` [+${resp.rewardPoints}pts]` : ''
        showAlert(translate('quickplay_16', 'The order has been placed') + pointsDesc)
        ctx.emit('created', resp)
      })
    }

    const calcStopPrice = (price: string, profit = false) => {
      const diff = profit ? product.value?.stopProfitOffset : -(product.value?.stopLossOffset ?? 0)
      return add(price, (diff || 0) * (isBuy.value ? 1 : -1))
    }

    return {
      isBuy,
      buy,
      sell,
      product,
      products,
      commit,
      progress,
      cost,
      calcStopPrice,
    }
  },
})
</script>

<style scoped>

</style>
